// @ts-nocheck
/**
 * Cart reward selector: OR-based offer UI + line item properties for tiered discount app.
 * - _cabin_bag path → stamps properties._cabin_bag = 'true' on merchandise lines + adds free cabin bag variant
 * - _july_offer path → stamps properties._july_offer = 'true' on eligible merchandise lines
 */
(function cartRewardSelector() {
  const SYNC_SOURCE = 'cart-reward-selector';
  const ATTR_SELECTED = 'selected_reward';
  const PROP_CABIN = '_cabin_bag';
  const PROP_JULY = '_july_offer';
  const PROP_EXCLUDED = '_cart_reward_excluded';
  const PROP_50_COMPARE = '_equalorabove50%discount';
  const PROP_20_OFF = '_cart_reward_20_off';

  /** @type {Set<number>} */
  const excludedRewardVariantIds = new Set();

  /** @type {Set<string>} */
  const excludedRewardLineKeys = new Set();

  function cfg() {
    return window.CART_REWARD_CONFIG || {};
  }

  function getExcludedProductTags() {
    const tags = cfg().excludedProductTags;
    if (Array.isArray(tags) && tags.length) return tags;
    if (cfg().excludedProductTag) return [cfg().excludedProductTag];
    return ['50% Off', 'Label_18K Gold Dial'];
  }

  function parseProductTagsFromDom(el) {
    const raw = el.getAttribute('data-product-tags');
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map((tag) => String(tag).trim()).filter(Boolean) : [];
    } catch (e) {
      return String(raw)
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
  }

  function lineHasExcludedProductTag(tags) {
    const excludedTags = getExcludedProductTags();
    return tags.some((tag) => excludedTags.includes(tag));
  }

  function refreshExcludedVariantIdsFromDom(cart) {
    excludedRewardVariantIds.clear();
    excludedRewardLineKeys.clear();

    document.querySelectorAll('[data-line-key][data-variant-id]').forEach((el) => {
      const lineKey = el.getAttribute('data-line-key');
      const variantId = Number(el.getAttribute('data-variant-id'));
      if (!lineKey || variantId <= 0) return;

      const rewardExcluded = el.getAttribute('data-reward-excluded') === 'true';
      const tagExcluded = lineHasExcludedProductTag(parseProductTagsFromDom(el));
      if (!rewardExcluded && !tagExcluded) return;

      excludedRewardLineKeys.add(lineKey);
      excludedRewardVariantIds.add(variantId);
    });

    const excludedTags = getExcludedProductTags();
    if (excludedTags.length) {
      document.querySelectorAll('[data-variant-id][data-product-tags]').forEach((el) => {
        if (el.getAttribute('data-reward-excluded') === 'true') return;
        const tagsJson = el.getAttribute('data-product-tags');
        if (!tagsJson) return;
        let tags = [];
        try {
          tags = JSON.parse(tagsJson);
        } catch (e) {
          return;
        }
        if (!Array.isArray(tags)) return;
        const hasExcludedTag = tags.some((t) => excludedTags.includes(String(t)));
        if (!hasExcludedTag) return;
        const id = Number(el.getAttribute('data-variant-id'));
        if (id > 0) excludedRewardVariantIds.add(id);
      });
    }

    if (!cart?.items?.length) return;

    document.querySelectorAll('[data-compare-at][data-line-key]').forEach((el) => {
      const compareAt = Number(el.getAttribute('data-compare-at'));
      const lineKey = el.getAttribute('data-line-key');
      if (!lineKey || compareAt <= 0) return;

      const item = cart.items.find((line) => line.key === lineKey);
      if (!item || isManagedPromoLine(item)) return;

      const unitCents = Number(item.original_price || item.price);
      if (unitCents <= 0 || compareAt <= unitCents) return;

      const discountPct = Math.round(((compareAt - unitCents) * 100) / compareAt);
      if (discountPct >= 50 || discountPct === 20) {
        excludedRewardLineKeys.add(lineKey);
        const variantId = Number(item.variant_id);
        if (variantId > 0) excludedRewardVariantIds.add(variantId);
      }
    });
  }

  function isRewardExcludedLine(item) {
    const p = item.properties || {};
    if (isTruthy(p[PROP_EXCLUDED])) return true;
    if (isTruthy(p[PROP_50_COMPARE])) return true;
    if (isTruthy(p[PROP_20_OFF])) return true;
    const lineKey = String(item?.key || '');
    if (lineKey && excludedRewardLineKeys.has(lineKey)) return true;
    const variantId = Number(item?.variant_id);
    return variantId > 0 && excludedRewardVariantIds.has(variantId);
  }

  function isEligibleRewardMerchandiseLine(item) {
    return !isManagedPromoLine(item) && !isRewardExcludedLine(item);
  }

  function hasRewardProperties(item) {
    return isJulyOfferLine(item) || hasCabinBagProperty(item);
  }

  function hasRewardEligibleMerchandise(cart) {
    return (cart?.items || []).some(isEligibleRewardMerchandiseLine);
  }

  function needsExcludedLineStrip(cart) {
    return (cart?.items || []).some(
      (item) => !isManagedPromoLine(item) && isRewardExcludedLine(item) && hasRewardProperties(item)
    );
  }

  function needsLineSync(cart, target) {
    if (!target || !cart?.items?.length) return false;

    const items = cart.items;

    if (needsExcludedLineStrip(cart)) return true;

    if (target === '_july_offer') {
      if (items.some(isCabinRewardLine)) return true;
      return items.some((item) => isEligibleRewardMerchandiseLine(item) && !isJulyOfferLine(item));
    }

    if (target === '_cabin_bag') {
      if (items.some((item) => isEligibleRewardMerchandiseLine(item) && isJulyOfferLine(item))) return true;
      if (items.some((item) => isEligibleRewardMerchandiseLine(item) && !hasCabinBagProperty(item))) return true;
      const cabinLines = items.filter(isCabinRewardLine);
      if (!cabinLines.length) return true;
      if (cabinLines.length > 1) return true;
      if (Number(cabinLines[0].quantity) !== 1) return true;
      if (items.some((item) => isCabinVariantLine(item) && !isCabinRewardLine(item))) return true;
      return false;
    }

    if (items.some(isCabinRewardLine)) return true;
    return items.some((item) => isEligibleRewardMerchandiseLine(item) && isJulyOfferLine(item));
  }

  function cfg() {
    return window.CART_REWARD_CONFIG || {};
  }

  function getExcludedProductTags() {
    const c = cfg();
    if (Array.isArray(c.excludedProductTags) && c.excludedProductTags.length) {
      return c.excludedProductTags.map((t) => String(t));
    }
    if (c.excludedProductTag) return [String(c.excludedProductTag)];
    return [];
  }

  function isEnabled() {
    const c = cfg();
    return !!c.enabled && !!c.cabinBagVariantId;
  }

  function tierConfig() {
    const c = cfg();
    return {
      orMinCents: Math.round(Number(c.tierOrMin || 400) * 100),
      comboMinCents: Math.round(Number(c.tierCabinComboMin || 751) * 100),
      discountBoostMinCents: Math.round(Number(c.tierDiscountBoostMin || 1501) * 100),
    };
  }

  function getOfferTierKey(subtotalCents) {
    const { orMinCents, comboMinCents, discountBoostMinCents } = tierConfig();
    if (subtotalCents >= discountBoostMinCents) return 'boost';
    if (subtotalCents >= comboMinCents) return 'combo';
    if (subtotalCents >= orMinCents) return 'or';
    return 'belowOr';
  }

  function getTierTexts(subtotalCents) {
    const texts = cfg().tierTexts || {};
    const tier = getOfferTierKey(subtotalCents);
    if (tier === 'belowOr') {
      return { belowOrHtml: texts.belowOr || null, cabinHtml: null, discountHtml: null };
    }
    const tierTexts = texts[tier] || {};
    return {
      belowOrHtml: texts.belowOr || null,
      cabinHtml: formatOfferLabelHtml(tierTexts.cabin || null),
      discountHtml: formatOfferLabelHtml(tierTexts.discount || null),
    };
  }

  /** Ensure "% OFF" rich-text lines break onto the next row after the lead-in (e.g. CABIN BAG + / FLAT). */
  function formatOfferLabelHtml(html) {
    if (!html || typeof html !== 'string') return html;

    let result = html;

    result = result.replace(
      /<em>\s*FLAT\s*<\/em>\s*<em>\s*<strong>(\s*\d+%)\s*<\/strong>\s*<\/em>\s*<em>\s*OFF\s*<\/em>/gi,
      'FLAT<br><strong>$1 OFF</strong>'
    );

    result = result.replace(
      /<strong>\s*FLAT\s+(\d+%)\s*OFF\s*<\/strong>/gi,
      'FLAT<br><strong>$1 OFF</strong>'
    );

    result = result.replace(
      /(Cabin Bag\s*\+)\s*<strong>(\s*\d+%\s*)<\/strong>\s*Off/gi,
      '$1<br><strong>$2Off</strong>'
    );

    result = result.replace(/(Cabin Bag\s*\+)(?!<br)\s*<strong>/gi, '$1<br><strong>');

    return result;
  }

  function isTruthy(val) {
    return val === true || val === 'true' || val === 'True';
  }

  function hasCabinBagProperty(item) {
    return isTruthy((item.properties || {})[PROP_CABIN]);
  }

  /** Free cabin bag SKU in cart (not merchandise stamped for tiered discount). */
  function isCabinRewardLine(item) {
    return isCabinVariantLine(item);
  }

  function isCabinVariantLine(item) {
    const variantId = Number(cfg().cabinBagVariantId);
    return variantId > 0 && Number(item?.variant_id) === variantId;
  }

  function isJulyOfferLine(item) {
    return isTruthy((item.properties || {})[PROP_JULY]);
  }

  function isManagedPromoLine(item) {
    const p = item.properties || {};
    if (isCabinRewardLine(item)) return true;
    if (isTruthy(p._is_buyxgety_freebie)) return true;
    if (isTruthy(p._auto_subtotal_freebie)) return true;
    if (isTruthy(p._is_lamp_freebie)) return true;
    return false;
  }

  /** Pre-reward merchandise line total — stable slab base before cart-reward extra discount. */
  function getMerchandiseLineSlabCents(item) {
    const originalLinePrice = Number(item?.original_line_price);
    if (originalLinePrice > 0) return originalLinePrice;
    const unitQty =
      Number(item?.original_price) > 0 && Number(item?.quantity) > 0
        ? Number(item.original_price) * Number(item.quantity)
        : 0;
    if (unitQty > 0) return unitQty;
    const linePrice = Number(item?.line_price);
    if (linePrice > 0) return linePrice;
    return Number(item?.final_line_price ?? 0);
  }

  function getMerchandiseSubtotalCents(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (isCabinRewardLine(item) || isRewardExcludedLine(item) || isTruthy((item.properties || {})._is_lamp_freebie)) return sum;
      return sum + getMerchandiseLineSlabCents(item);
    }, 0);
  }

  function showOrChoice(subtotalCents) {
    return subtotalCents >= tierConfig().orMinCents;
  }

  function getDiscountPct(subtotalCents) {
    return subtotalCents >= tierConfig().discountBoostMinCents ? 25 : 20;
  }

  function isValidSelection(selected, subtotalCents) {
    if (!selected) return subtotalCents < tierConfig().orMinCents;
    if (selected === '_july_offer') return true;
    if (selected === '_cabin_bag') return showOrChoice(subtotalCents);
    return false;
  }

  /** Infer active reward from stamped line properties when cart attribute is empty. */
  function inferSelectedRewardFromLines(cart) {
    const items = cart?.items || [];
    if (items.some(isCabinRewardLine)) return '_cabin_bag';

    const eligible = items.filter(isEligibleRewardMerchandiseLine);
    if (!eligible.length) return '';

    const anyJuly = eligible.some(isJulyOfferLine);
    const anyCabin = eligible.some(hasCabinBagProperty);
    const allJuly = eligible.every(isJulyOfferLine);
    const allCabin = eligible.every(hasCabinBagProperty);

    if (allCabin && anyCabin) return '_cabin_bag';
    if (allJuly && anyJuly) return '_july_offer';
    if (anyCabin && !anyJuly) return '_cabin_bag';
    if (anyJuly && !anyCabin) return '_july_offer';

    return '';
  }

  function getCartSectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
      const id = node.getAttribute('data-section-id');
      if (id) sectionIds.add(id);
    });
    document.querySelectorAll('[data-cart-reward-selector][data-section-id]').forEach((node) => {
      const id = node.getAttribute('data-section-id');
      if (id) sectionIds.add(id);
    });
    return Array.from(sectionIds);
  }

  function getSectionsPayload() {
    const sectionIds = getCartSectionIds();
    return {
      sectionIds,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
  }

  function getCheckoutButtons() {
    const byId = document.querySelectorAll('#checkout');
    if (byId.length) return Array.from(byId);
    return Array.from(document.querySelectorAll('button.cart__checkout-button[name="checkout"]'));
  }

  function setCheckoutLocked(locked) {
    getCheckoutButtons().forEach((btn) => {
      if (locked) {
        if (btn.dataset.rewardCheckoutPrevDisabled === undefined) {
          btn.dataset.rewardCheckoutPrevDisabled = btn.disabled ? '1' : '0';
        }
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      } else {
        if (btn.dataset.rewardCheckoutPrevDisabled !== undefined) {
          btn.disabled = btn.dataset.rewardCheckoutPrevDisabled === '1';
          delete btn.dataset.rewardCheckoutPrevDisabled;
        }
        btn.removeAttribute('aria-busy');
      }
    });
  }

  let cartSettlingLockCount = 0;

  function ensureCartSettlingOverlays() {
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component.querySelector('.cart-settling-overlay')) return;

      const overlay = document.createElement('div');
      overlay.className = 'cart-settling-overlay';
      overlay.innerHTML = `
        <div class="cart-settling-overlay__loader" role="status" aria-live="polite" aria-label="Updating cart">
          <span class="cart-settling-overlay__dot"></span>
          <span class="cart-settling-overlay__dot"></span>
          <span class="cart-settling-overlay__dot"></span>
        </div>
      `;
      component.appendChild(overlay);
    });
  }

  function applyCartSettlingUi() {
    const active = cartSettlingLockCount > 0;
    ensureCartSettlingOverlays();
    setCheckoutLocked(active);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      el.classList.toggle('is-cart-settling', active);
    });
    document.querySelectorAll('[data-cart-reward-selector]').forEach((el) => {
      el.classList.toggle('is-syncing', active);
    });
  }

  function pushCartSettlingLock() {
    cartSettlingLockCount += 1;
    applyCartSettlingUi();
  }

  function popCartSettlingLock() {
    if (cartSettlingLockCount <= 0) return;
    cartSettlingLockCount -= 1;
    applyCartSettlingUi();
  }

  function refreshCartSettlingUiAfterMorph() {
    refreshExcludedVariantIdsFromDom();
    if (cartSettlingLockCount <= 0) return;
    ensureCartSettlingOverlays();
    applyCartSettlingUi();
  }

  let reconcileQueue = Promise.resolve();
  let rewardApplyQueue = Promise.resolve();
  let syncInProgress = false;
  let userSelectionInProgress = false;
  /** @type {object | null} */
  let cachedCart = null;
  let cachedCartAt = 0;
  const CART_CACHE_MS = 8000;
  let reconcileDebounceTimer = null;

  function adoptCartResponse(data) {
    if (!isCartResponse(data)) return null;
    setCartCache(data);
    return data;
  }

  async function batchCartUpdate(payload, sectionsPayload, { includeSections = false } = {}) {
    const body = { ...payload };
    if (includeSections && sectionsPayload?.sections) {
      body.sections = sectionsPayload.sections;
      body.sections_url = sectionsPayload.sections_url;
    }
    const { ok, data } = await cartUpdate(body);
    const cart = adoptCartResponse(data);
    return { ok, data, cart, sections: data?.sections || null };
  }

  function getMerchandisePropertyUpdates(cart, reward) {
    const updates = [];
    for (const item of cart?.items || []) {
      if (reward === 'strip_excluded') {
        if (!isManagedPromoLine(item) && isRewardExcludedLine(item) && hasRewardProperties(item)) {
          updates.push({
            item,
            properties: cleanProperties(item.properties, { removeJuly: true, removeCabin: true }),
          });
        }
        continue;
      }
      if (!isEligibleRewardMerchandiseLine(item)) continue;
      if (reward === '_july_offer') {
        if (!isJulyOfferLine(item) || hasCabinBagProperty(item)) {
          updates.push({
            item,
            properties: cleanProperties(item.properties, { addJuly: true, removeCabin: true }),
          });
        }
      } else if (reward === '_cabin_bag') {
        if (!hasCabinBagProperty(item) || isJulyOfferLine(item)) {
          updates.push({
            item,
            properties: cleanProperties(item.properties, { addCabin: true, removeJuly: true }),
          });
        }
      } else if (reward === 'clear_rewards') {
        if (hasRewardProperties(item)) {
          updates.push({
            item,
            properties: cleanProperties(item.properties, { removeJuly: true, removeCabin: true }),
          });
        }
      }
    }
    return updates;
  }

  /**
   * Batch remove lines then re-add with updated properties (2 requests max).
   * @param {object} cart
   * @param {Array<{ item: object, properties: object }>} lineUpdates
   */
  async function batchUpdateLineProperties(cart, lineUpdates, sectionsPayload, { includeSections = false } = {}) {
    if (!lineUpdates.length) {
      return { ok: true, mutated: false, cart, sections: null };
    }

    const updates = Object.fromEntries(lineUpdates.map(({ item }) => [item.key, 0]));
    const { ok: removeOk, cart: cartAfterRemove } = await batchCartUpdate({ updates }, sectionsPayload);
    if (!removeOk) return { ok: false, mutated: false, cart, sections: null };

    let workingCart = cartAfterRemove || cart;
    const addItems = lineUpdates.map(({ item, properties }) => ({
      id: Number(item.variant_id),
      quantity: Math.max(1, Number(item.quantity || 1)),
      properties: propertiesToCartAddFormat(properties),
    }));

    const { ok: addOk, data: addData } = await cartAddItems(addItems, sectionsPayload, { includeSections });
    if (!addOk) return { ok: false, mutated: false, cart: workingCart, sections: null };

    workingCart = mergeCartAddResponse(workingCart, addData) || workingCart;
    if (isCartResponse(workingCart)) setCartCache(workingCart);

    return { ok: true, mutated: true, cart: workingCart, sections: addData?.sections || null };
  }

  async function batchRemoveLinesByKey(cart, lineKeys, sectionsPayload, options = {}) {
    const uniqueKeys = [...new Set(lineKeys.filter(Boolean))];
    if (!uniqueKeys.length) {
      return { ok: true, mutated: false, cart, sections: null };
    }

    const payload = { updates: Object.fromEntries(uniqueKeys.map((key) => [key, 0])) };
    if (options.attributes) payload.attributes = options.attributes;

    const { ok, cart: nextCart, sections } = await batchCartUpdate(payload, sectionsPayload, {
      includeSections: options.includeSections === true,
    });

    return { ok, mutated: ok, cart: nextCart || cart, sections };
  }

  async function applyJulyOfferToCart(cart, sectionsPayload, attrValue) {
    let workingCart = cart;
    let sections = null;
    let mutated = false;

    const cabinKeys = (workingCart.items || []).filter(isCabinRewardLine).map((item) => item.key);
    const merchUpdates = getMerchandisePropertyUpdates(workingCart, '_july_offer');
    const merchKeys = merchUpdates.map(({ item }) => item.key);
    const removalKeys = [...new Set([...cabinKeys, ...merchKeys])];

    const updatePayload = {};
    if (removalKeys.length) {
      updatePayload.updates = Object.fromEntries(removalKeys.map((key) => [key, 0]));
    }
    if (attrValue !== undefined) {
      updatePayload.attributes = { [ATTR_SELECTED]: attrValue || '' };
    }

    const morphOnRemoveOnly = removalKeys.length > 0 && merchUpdates.length === 0;
    if (removalKeys.length || (attrValue !== undefined && !merchUpdates.length)) {
      const { ok, cart: nextCart, sections: removeSections } = await batchCartUpdate(
        updatePayload,
        sectionsPayload,
        { includeSections: morphOnRemoveOnly }
      );
      if (!ok) return { ok: false, failed: true, cart: workingCart, sections: null, mutated: false };
      if (removalKeys.length) mutated = true;
      workingCart = nextCart || workingCart;
      sections = removeSections;
    }

    if (merchUpdates.length) {
      const addItems = merchUpdates.map(({ item, properties }) => ({
        id: Number(item.variant_id),
        quantity: Math.max(1, Number(item.quantity || 1)),
        properties: propertiesToCartAddFormat(properties),
      }));

      const { ok: addOk, data: addData } = await cartAddItems(addItems, sectionsPayload, { includeSections: true });
      if (!addOk) return { ok: false, failed: true, cart: workingCart, sections: null, mutated };

      workingCart = mergeCartAddResponse(workingCart, addData) || workingCart;
      if (isCartResponse(workingCart)) setCartCache(workingCart);
      sections = addData?.sections || sections;
      mutated = true;
    }

    return { ok: true, failed: false, cart: workingCart, sections, mutated };
  }

  async function applyCabinBagToCart(cart, sectionsPayload, attrValue) {
    let workingCart = cart;
    let sections = null;
    let mutated = false;

    const merchUpdates = getMerchandisePropertyUpdates(workingCart, '_cabin_bag');
    const merchKeysToReAdd = merchUpdates.map(({ item }) => item.key);

    const strayKeys = (workingCart.items || [])
      .filter((item) => isCabinVariantLine(item) && !isCabinRewardLine(item))
      .map((item) => item.key);
    const rewardLines = (workingCart.items || []).filter(isCabinRewardLine);
    const duplicateCabinKeys = rewardLines.slice(1).map((item) => item.key);
    const primaryCabin = rewardLines[0];
    const cabinQtyFixKey =
      primaryCabin && Number(primaryCabin.quantity) !== 1 ? primaryCabin.key : null;

    const removalKeys = [...new Set([...merchKeysToReAdd, ...strayKeys, ...duplicateCabinKeys])];
    const hasValidCabinLine = !!primaryCabin && !cabinQtyFixKey;
    const variantId = Number(cfg().cabinBagVariantId);

    const addItems = merchUpdates.map(({ item, properties }) => ({
      id: Number(item.variant_id),
      quantity: Math.max(1, Number(item.quantity || 1)),
      properties: propertiesToCartAddFormat(properties),
    }));

    if (!hasValidCabinLine && variantId > 0) {
      addItems.push({
        id: variantId,
        quantity: 1,
        properties: propertiesToCartAddFormat({ [PROP_CABIN]: 'true' }),
      });
    }

    const updatePayload = {};
    const lineUpdates = {};
    removalKeys.forEach((key) => {
      lineUpdates[key] = 0;
    });
    if (cabinQtyFixKey) lineUpdates[cabinQtyFixKey] = 1;
    if (Object.keys(lineUpdates).length) updatePayload.updates = lineUpdates;
    if (attrValue !== undefined) {
      updatePayload.attributes = { [ATTR_SELECTED]: attrValue || '' };
    }

    const needsAdd = addItems.length > 0;
    const needsUpdate = Object.keys(lineUpdates).length > 0 || attrValue !== undefined;
    const morphOnUpdateOnly = needsUpdate && !needsAdd;

    if (needsUpdate) {
      const { ok, cart: nextCart, sections: updateSections } = await batchCartUpdate(
        updatePayload,
        sectionsPayload,
        { includeSections: morphOnUpdateOnly }
      );
      if (!ok) return { ok: false, failed: true, cart: workingCart, sections: null, mutated: false };
      if (Object.keys(lineUpdates).length) mutated = true;
      workingCart = nextCart || workingCart;
      sections = updateSections;
    }

    if (needsAdd) {
      const { ok: addOk, data: addData } = await cartAddItems(addItems, sectionsPayload, { includeSections: true });
      if (!addOk) return { ok: false, failed: true, cart: workingCart, sections: null, mutated };

      workingCart = mergeCartAddResponse(workingCart, addData) || workingCart;
      if (isCartResponse(workingCart)) setCartCache(workingCart);
      sections = addData?.sections || sections;
      mutated = true;
    }

    return { ok: true, failed: false, cart: workingCart, sections, mutated };
  }

  async function applyClearRewardsToCart(cart, sectionsPayload, attrValue) {
    let workingCart = cart;
    let sections = null;
    let mutated = false;

    const cabinKeys = (workingCart.items || []).filter(isCabinRewardLine).map((item) => item.key);
    const merchUpdates = getMerchandisePropertyUpdates(workingCart, 'clear_rewards');
    const removalKeys = [...new Set([...cabinKeys, ...merchUpdates.map(({ item }) => item.key)])];

    if (removalKeys.length) {
      const { ok, cart: nextCart, sections: removeSections } = await batchRemoveLinesByKey(
        workingCart,
        removalKeys,
        sectionsPayload
      );
      if (!ok) return { ok: false, failed: true, cart: workingCart, sections: null, mutated: false };
      mutated = true;
      workingCart = nextCart || workingCart;
      sections = removeSections;
    }

    if (merchUpdates.length) {
      const addItems = merchUpdates.map(({ item, properties }) => ({
        id: Number(item.variant_id),
        quantity: Math.max(1, Number(item.quantity || 1)),
        properties: propertiesToCartAddFormat(properties),
      }));

      const { ok: addOk, data: addData } = await cartAddItems(addItems, sectionsPayload, { includeSections: true });
      if (!addOk) return { ok: false, failed: true, cart: workingCart, sections: null, mutated };
      workingCart = mergeCartAddResponse(workingCart, addData) || workingCart;
      if (isCartResponse(workingCart)) setCartCache(workingCart);
      sections = addData?.sections || sections;
      mutated = true;
    }

    if (attrValue !== undefined) {
      const { ok, cart: attrCart, sections: attrSections } = await batchCartUpdate(
        { attributes: { [ATTR_SELECTED]: attrValue || '' } },
        sectionsPayload,
        { includeSections: !sections }
      );
      if (!ok) return { ok: false, failed: true, cart: workingCart, sections, mutated };
      workingCart = attrCart || workingCart;
      if (!sections) sections = attrSections;
    }

    return { ok: true, failed: false, cart: workingCart, sections, mutated };
  }

  async function batchStripExcludedRewardLines(cart, sectionsPayload) {
    const updates = getMerchandisePropertyUpdates(cart, 'strip_excluded');
    if (!updates.length) return { ok: true, mutated: false, cart, sections: null };
    return batchUpdateLineProperties(cart, updates, sectionsPayload, { includeSections: false });
  }

  function setCartCache(cart) {
    if (isCartResponse(cart)) {
      cachedCart = cart;
      cachedCartAt = Date.now();
    }
  }

  function invalidateCartCache() {
    cachedCart = null;
    cachedCartAt = 0;
  }

  /**
   * Serialize all reward cart mutations so concurrent offer switches cannot interleave.
   * @param {() => Promise<unknown>} fn
   */
  function runExclusiveRewardApply(fn) {
    const run = rewardApplyQueue.then(() => fn());
    rewardApplyQueue = run.catch(() => {});
    return run;
  }

  function isRewardMutationLocked() {
    return userSelectionInProgress || syncInProgress;
  }

  function getDefaultRewardForSubtotal(subtotalCents) {
    return showOrChoice(subtotalCents) ? '_cabin_bag' : '_july_offer';
  }

  function getEffectiveSelectedReward(cart) {
    const subtotalCents = getMerchandiseSubtotalCents(cart);
    let selected = String(cart?.attributes?.[ATTR_SELECTED] || '');

    if (!selected) {
      if (showOrChoice(subtotalCents)) {
        selected = '_cabin_bag';
      } else {
        selected = inferSelectedRewardFromLines(cart) || '_july_offer';
      }
    }

    if (selected === '_cabin_bag' && !showOrChoice(subtotalCents)) {
      selected = '_july_offer';
    }
    if (selected && !isValidSelection(selected, subtotalCents)) {
      selected = getDefaultRewardForSubtotal(subtotalCents);
    }

    return selected;
  }

  async function getCartRewardLinePropertiesForAdd() {
    if (!isEnabled()) return {};

    const cart = await fetchCart({ force: true });
    if (!cart) return {};

    const reward = getEffectiveSelectedReward(cart);
    let props = {};
    if (reward === '_july_offer') props = { [PROP_JULY]: 'true' };
    else if (reward === '_cabin_bag') props = { [PROP_CABIN]: 'true' };

    return props;
  }

  function isCartResponse(data) {
    return !!data && Array.isArray(data.items) && typeof data.item_count === 'number';
  }

  function isCartAddResponse(data) {
    return !!data && Array.isArray(data.items) && typeof data.item_count !== 'number';
  }

  /**
   * @param {object | null | undefined} cart
   * @param {object | null | undefined} addData
   */
  function mergeCartAddResponse(cart, addData) {
    if (!isCartResponse(cart) || !addData?.items?.length) return cart;
    const items = [...cart.items, ...addData.items];
    const merged = {
      ...cart,
      items,
      item_count: items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    };

    return merged;
  }

  /**
   * @param {Record<string, string>} sections
   * @param {string[]} sectionIds
   */
  async function morphCartSectionsFromResponse(sections, sectionIds) {
    if (!sections || !sectionIds?.length) return false;
    try {
      const { morphSection } = await import('@theme/section-renderer');
      await Promise.all(
        sectionIds.map((id) => {
          const html = sections[id];
          return html
            ? morphSection(id, html, 'full', { cartLineKeys: true })
            : Promise.resolve();
        })
      );

      refreshCartSettlingUiAfterMorph();
      if (typeof window.scheduleCartSavingsUpdate === 'function') {
        window.scheduleCartSavingsUpdate();
      } else if (typeof window.updateCartSavings === 'function') {
        window.updateCartSavings();
      }
      return true;
    } catch (e) {

      return false;
    }
  }

  async function fetchCart(options = {}) {
    const force = options.force === true;
    if (!force && cachedCart && Date.now() - cachedCartAt < CART_CACHE_MS) {

      return cachedCart;
    }
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) {

      return null;
    }
    const cart = await res.json().catch(() => null);
    setCartCache(cart);

    return cart;
  }

  async function cartAddItems(items, sectionsPayload, { includeSections = false } = {}) {
    const body = { items };
    if (includeSections && sectionsPayload?.sections) {
      body.sections = sectionsPayload.sections;
      body.sections_url = sectionsPayload.sections_url;
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    const ok = res.ok && !data.status;

    return { ok, data };
  }

  async function cartUpdate(body) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    const ok = res.ok && !data.status;

    return { ok, data };
  }

  async function cartChange(lineKey, quantity, sectionsPayload, { includeSections = false } = {}) {
    const body = { id: lineKey, quantity };
    if (includeSections && sectionsPayload?.sections) {
      body.sections = sectionsPayload.sections;
      body.sections_url = sectionsPayload.sections_url;
    }
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    const ok = res.ok && !data.status;
    const lineBefore = isCartResponse(data)
      ? data.items.find((item) => item.key === lineKey)
      : null;

    return { ok, data };
  }

  function resolveCartItemCount(cart) {
    if (!cart) return 0;
    if (Array.isArray(cart.items)) {
      return cart.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    }
    return Number(cart.item_count ?? 0);
  }

  function publishCartUpdate(cart, sections) {
    const itemCount = resolveCartItemCount(cart);

    if (typeof window.syncCartDrawerEmptyState === 'function') {
      window.syncCartDrawerEmptyState(itemCount === 0);
    }
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SYNC_SOURCE,
          data: { itemCount, source: SYNC_SOURCE, sections: sections || {} },
        },
      })
    );
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: SYNC_SOURCE,
        cartData: cart,
        itemCount,
        sections: sections || {},
      });
    }
  }

  function cleanProperties(properties, { addJuly, removeJuly, removeCabin, addCabin }) {
    const next = { ...(properties || {}) };
    if (removeJuly) delete next[PROP_JULY];
    if (removeCabin) delete next[PROP_CABIN];
    if (addJuly) next[PROP_JULY] = 'true';
    if (addCabin) next[PROP_CABIN] = 'true';
    return next;
  }

  function propertiesToCartAddFormat(props) {
    const out = {};
    Object.entries(props || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      out[k] = String(v);
    });
    return out;
  }

  async function cartAddItem(item, sectionsPayload, options = {}) {
    return cartAddItems([item], sectionsPayload, options);
  }

  async function removeCabinBagLines(cart, sectionsPayload, { includeSections = false } = {}) {
    const cabinKeys = (cart.items || []).filter(isCabinRewardLine).map((item) => item.key);
    if (!cabinKeys.length) return { mutated: false, cart, sections: null };
    const result = await batchRemoveLinesByKey(cart, cabinKeys, sectionsPayload, { includeSections });
    return { mutated: result.mutated, cart: result.cart || cart, sections: result.sections };
  }

  async function applyRewardToCart(selectedReward, initialCart = null, options = {}) {
    return runExclusiveRewardApply(() => applyRewardToCartInner(selectedReward, initialCart, options));
  }

  async function applyRewardToCartInner(selectedReward, initialCart = null, options = {}) {
    syncInProgress = true;
    try {
      return await applyRewardToCartMutate(selectedReward, initialCart, options);
    } finally {
      syncInProgress = false;
    }
  }

  async function applyRewardToCartMutate(selectedReward, initialCart = null, options = {}) {
    const attrValue = options.selectedRewardAttr ?? selectedReward;
    const sectionsPayload = getSectionsPayload();
    let cart =
      initialCart && isCartResponse(initialCart) ? initialCart : await fetchCart({ force: true });

    if (!cart?.items) {
      return { cart, morphed: false, failed: false };
    }

    let mutated = false;
    let sectionsFromResponse = null;
    let failed = false;

    const stripResult = await batchStripExcludedRewardLines(cart, sectionsPayload);
    if (!stripResult.ok) {
      failed = true;
      cart = adoptCartResponse(stripResult.cart) || (await fetchCart({ force: true })) || cart;
    } else if (stripResult.mutated) {
      mutated = true;
      cart = stripResult.cart || cart;
      sectionsFromResponse = stripResult.sections || sectionsFromResponse;
    }

    let applyResult = { ok: true, failed: false, cart, sections: null, mutated: false };
    if (!failed && selectedReward === '_cabin_bag') {
      applyResult = await applyCabinBagToCart(cart, sectionsPayload, attrValue);
    } else if (!failed && selectedReward === '_july_offer') {
      applyResult = await applyJulyOfferToCart(cart, sectionsPayload, attrValue);
    } else if (!failed) {
      applyResult = await applyClearRewardsToCart(cart, sectionsPayload, attrValue);
    }

    if (!applyResult.ok || applyResult.failed) {
      failed = true;
      cart = applyResult.cart || cart;
    } else if (applyResult.mutated) {
      mutated = true;
      cart = applyResult.cart || cart;
      sectionsFromResponse = applyResult.sections || sectionsFromResponse;
    }

    let morphed = false;
    if (mutated || failed) {
      if (!isCartResponse(cart)) {
        cart = (await fetchCart({ force: true })) || cart;
      }
      setCartCache(cart);

      // Morph once here; omit sections from the event so component-cart-items does not morph again.
      if (sectionsFromResponse) {
        morphed = await morphCartSectionsFromResponse(sectionsFromResponse, sectionsPayload.sectionIds);
      }
      publishCartUpdate(cart, {});
      if (!morphed) {
        await morphCartSections(sectionsPayload.sectionIds);
        morphed = true;
      }
    }

    return { cart, morphed, failed };
  }

  async function morphCartSections(sectionIds) {
    if (!sectionIds?.length) {

      return;
    }
    try {
      const { sectionRenderer } = await import('@theme/section-renderer');
      await Promise.all(sectionIds.map((id) => sectionRenderer.renderSection(id, { cache: false })));

      refreshCartSettlingUiAfterMorph();
      if (typeof window.scheduleCartSavingsUpdate === 'function') {
        window.scheduleCartSavingsUpdate();
      } else if (typeof window.updateCartSavings === 'function') {
        window.updateCartSavings();
      }
    } catch (e) {

    }
  }

  async function setSelectedReward(selectedReward, { morphSections = false } = {}) {
    const sectionsPayload = getSectionsPayload();
    const attributes = { [ATTR_SELECTED]: selectedReward || '' };
    const body = { attributes };
    if (sectionsPayload.sections) {
      body.sections = sectionsPayload.sections;
      body.sections_url = sectionsPayload.sections_url;
    }
    const { ok, data } = await cartUpdate(body);
    if (!ok) return null;
    if (isCartResponse(data)) setCartCache(data);
    if (morphSections && data?.sections) {
      await morphCartSectionsFromResponse(data.sections, sectionsPayload.sectionIds);
    }
    return data;
  }

  async function reconcileFromCart(cart, options = {}) {
    if (!isEnabled() || syncInProgress || userSelectionInProgress) {

      return;
    }
    if (!cart?.items?.length) {

      return;
    }

    refreshExcludedVariantIdsFromDom();

    if (needsExcludedLineStrip(cart)) {
      syncInProgress = true;
      try {
        const stripResult = await batchStripExcludedRewardLines(cart, getSectionsPayload());
        if (stripResult.cart) cart = stripResult.cart;
      } finally {
        syncInProgress = false;
      }
    }

    if (!hasRewardEligibleMerchandise(cart)) {
      let mutated = false;
      syncInProgress = true;
      try {
        const sectionsPayload = getSectionsPayload();
        const cabinResult = await removeCabinBagLines(cart, sectionsPayload, { includeSections: false });
        if (cabinResult.cart) cart = cabinResult.cart;
        if (cabinResult.mutated) mutated = true;
        if (cabinResult.mutated || String(cart.attributes?.[ATTR_SELECTED] || '')) {
          const cleared = await setSelectedReward('', { morphSections: true });
          if (isCartResponse(cleared)) cart = cleared;
        }
        if (mutated) {
          if (!isCartResponse(cart)) {
            cart = (await fetchCart({ force: true })) || cart;
          }
          setCartCache(cart);
          publishCartUpdate(cart, {});
        }
      } finally {
        syncInProgress = false;
      }
      document.querySelectorAll('[data-cart-reward-selector]').forEach((root) => {
        root.hidden = true;
      });
      return;
    }

    const subtotalCents = getMerchandiseSubtotalCents(cart);
    const target = getEffectiveSelectedReward(cart);

    let freshCart = cart;

    if (target && !needsLineSync(cart, target)) {
      if (target !== String(cart.attributes?.[ATTR_SELECTED] || '')) {

        const attrCart = await setSelectedReward(target, { morphSections: true });
        if (isCartResponse(attrCart)) freshCart = attrCart;
      }
      updateSelectorUiState(
        freshCart,
        String(freshCart.attributes?.[ATTR_SELECTED] || target),
        getMerchandiseSubtotalCents(freshCart)
      );


      return;
    }

    if (target && needsLineSync(cart, target)) {
      syncInProgress = true;
      try {
        const result = await applyRewardToCart(target, cart, { selectedRewardAttr: target });
        freshCart = result?.cart || freshCart;
      } finally {
        syncInProgress = false;
      }
    }

    updateSelectorUiState(
      freshCart,
      String(freshCart.attributes?.[ATTR_SELECTED] || target),
      getMerchandiseSubtotalCents(freshCart)
    );


  }

  function scheduleReconcile(cart, options = {}) {
    if (isRewardMutationLocked() && !options.force) {

      return Promise.resolve();
    }

    if (userSelectionInProgress && !options.force) {

      return Promise.resolve();
    }

    if (isCartResponse(cart)) {
      setCartCache(cart);
    }



    const delay = options.immediate ? 0 : 120;

    return new Promise((resolve) => {
      clearTimeout(reconcileDebounceTimer);
      reconcileDebounceTimer = setTimeout(() => {
        reconcileDebounceTimer = null;
        const job = reconcileQueue.then(async () => {
          pushCartSettlingLock();
          try {
            await reconcileFromCart(cart, options);

          } catch (e) {

            console.warn(SYNC_SOURCE, e);
          } finally {
            popCartSettlingLock();
            resolve();
          }
        });
        reconcileQueue = job.catch(() => {});
      }, delay);
    });
  }

  function pushGtm(name, payload) {
    window.pushGtmEvent?.({ event: name, ...payload });
  }

  function updateSelectorUiState(cart, selected, subtotalCents) {
    const subtotal = subtotalCents ?? getMerchandiseSubtotalCents(cart);
    const discountPct = getDiscountPct(subtotal);
    const orChoice = showOrChoice(subtotal);
    const eligible = hasRewardEligibleMerchandise(cart);
    const effectiveSelected = selected || getEffectiveSelectedReward(cart);

    document.querySelectorAll('[data-cart-reward-selector]').forEach((root) => {
      root.hidden = !eligible;
      if (!eligible) return;

      root.dataset.merchandiseSubtotalCents = String(subtotal);
      root.dataset.showOr = orChoice ? 'true' : 'false';
      root.dataset.selected = effectiveSelected || '';
      root.classList.toggle('cart-reward-selector--below-threshold', !orChoice);

      root.querySelectorAll('[data-reward-picker]').forEach((el) => {
        el.hidden = !orChoice;
      });
      root.querySelectorAll('[data-reward-below-threshold]').forEach((el) => {
        el.hidden = orChoice;
      });

      const tierTexts = getTierTexts(subtotal);

      const belowThresholdText = root.querySelector('[data-reward-below-threshold-text]');
      if (belowThresholdText) {
        if (tierTexts.belowOrHtml) {
          belowThresholdText.innerHTML = tierTexts.belowOrHtml;
        } else {
          belowThresholdText.textContent = `${getDiscountPct(subtotal)}% discount on your Purchase`;
        }
      }

      const cabinLabel = root.querySelector('[data-cabin-label]');
      if (cabinLabel && tierTexts.cabinHtml) {
        cabinLabel.innerHTML = tierTexts.cabinHtml;
      }
      const discountLabel = root.querySelector('[data-discount-label]');
      if (discountLabel && tierTexts.discountHtml) {
        discountLabel.innerHTML = tierTexts.discountHtml;
      }

      root.querySelectorAll('[data-reward-option]').forEach((btn) => {
        const option = btn.getAttribute('data-reward-option');
        const isSelected = option === effectiveSelected;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      const discountBadge = root.querySelector('[data-discount-badge]');
      if (discountBadge) discountBadge.textContent = `${discountPct}%`;
    });
  }

  async function onOptionSelect(option) {
    if (!isEnabled()) return;

    if (userSelectionInProgress) {
      return;
    }

    userSelectionInProgress = true;
    pushCartSettlingLock();
    let cart = null;
    let previous = '';
    let subtotalCents = 0;

    try {
      const cacheFresh = cachedCart && Date.now() - cachedCartAt < 3000;
      cart = cacheFresh ? cachedCart : await fetchCart({ force: true });
      if (!cart) return;

      subtotalCents = getMerchandiseSubtotalCents(cart);
      if (option === '_cabin_bag' && !showOrChoice(subtotalCents)) {
        return;
      }

      previous = String(cart.attributes?.[ATTR_SELECTED] || '');
      if (previous === option) {
        return;
      }

      updateSelectorUiState(cart, option, subtotalCents);

      const lineSyncRequired = needsLineSync(cart, option);

      if (!lineSyncRequired) {
        const attrCart = await setSelectedReward(option, { morphSections: true });
        const freshCart = isCartResponse(attrCart)
          ? attrCart
          : { ...cart, attributes: { ...(cart.attributes || {}), [ATTR_SELECTED]: option } };

        updateSelectorUiState(freshCart, option, subtotalCents);
        pushGtm(previous ? 'reward_changed' : 'reward_selected', {
          reward_id: option,
          previous_reward_id: previous || null,
          cart_value: subtotalCents / 100,
        });
        pushGtm('reward_applied', { reward_id: option, status: 'success' });
        return;
      }

      const { cart: updatedCart, morphed, failed } = await applyRewardToCart(option, cart, {
        selectedRewardAttr: option,
      });
      if (failed) {
        await setSelectedReward(previous || '');
        updateSelectorUiState(cart, previous, subtotalCents);
        await morphCartSections(getCartSectionIds());
        return;
      }
      if (!morphed) {
        await morphCartSections(getCartSectionIds());
      }

      updateSelectorUiState(updatedCart || cart, option, subtotalCents);

      pushGtm(previous ? 'reward_changed' : 'reward_selected', {
        reward_id: option,
        previous_reward_id: previous || null,
        cart_value: subtotalCents / 100,
      });
      pushGtm('reward_applied', { reward_id: option, status: 'success' });
    } catch (error) {
      if (cart) updateSelectorUiState(cart, previous, subtotalCents);
      throw error;
    } finally {
      userSelectionInProgress = false;
      popCartSettlingLock();
    }
  }

  function onCartUpdate(event) {
    const source = event?.detail?.sourceId || event?.detail?.data?.source || '';


    if (!isEnabled() || isRewardMutationLocked()) {

      return;
    }
    if (source === SYNC_SOURCE && !event?.detail?.forceReconcile) {

      return;
    }

    const cart = event?.detail?.resource;
    if (cart?.items) {
      setCartCache(cart);
      scheduleReconcile(cart);
    } else {

      fetchCart({ force: true }).then((c) => c && scheduleReconcile(c));
    }
  }

  async function validateCartBeforeCheckout() {
    const checkoutButton = document.querySelector(
      'cart-drawer-component button.cart__checkout-button[name="checkout"], button.cart__checkout-button[name="checkout"]'
    );
    if (typeof window.validateCartGiftMessagePairing === 'function') {
      if (!window.validateCartGiftMessagePairing(checkoutButton)) return false;
    } else if (!isGiftMessagePairingComplete(checkoutButton)) {
      return false;
    }

    let cart = await fetchCart({ force: true });
    if (!cart?.items?.length) return false;

    refreshExcludedVariantIdsFromDom(cart);
    if (!needsExcludedLineStrip(cart)) return true;

    const strip = await batchStripExcludedRewardLines(cart, getSectionsPayload());
    cart = strip.cart || (await fetchCart({ force: true }));
    if (!cart) return false;

    refreshExcludedVariantIdsFromDom(cart);
    return !needsExcludedLineStrip(cart);
  }

  async function proceedToCheckout() {
    if (cartSettlingLockCount > 0) return;
    pushCartSettlingLock();
    try {
      const ok = await validateCartBeforeCheckout();
      if (ok) window.location.assign('/checkout');
    } catch (e) {
      console.warn(SYNC_SOURCE, e);
    } finally {
      popCartSettlingLock();
    }
  }

  function isGiftMessagePairingComplete(trigger) {
    if (typeof window.validateCartGiftMessagePairing === 'function') {
      return window.validateCartGiftMessagePairing(trigger);
    }

    const surface = trigger instanceof Element ? trigger.closest('cart-drawer-component') || document : document;
    const details = surface.querySelector('cart-note details, accordion-custom details');
    if (details instanceof HTMLDetailsElement && !details.open) return true;

    const sender = surface.querySelector('input[name="attributes[sender_name]"]');
    const email = surface.querySelector('input[name="attributes[email_receipt]"]');
    const message = surface.querySelector('textarea[name="note"]');
    if (!sender && !email && !message) return true;

    const senderValue = (sender?.value || '').trim();
    const emailValue = (email?.value || '').trim();
    const messageValue = (message?.value || '').trim();
    const senderMissing = sender instanceof HTMLInputElement && !senderValue;
    if (!senderValue && !emailValue && !messageValue) return true;
    if (!senderMissing && emailValue && messageValue) {
      return !(email instanceof HTMLInputElement && email.validity.typeMismatch);
    }

    if (details instanceof HTMLDetailsElement) details.open = true;
    const missing = senderMissing ? sender : !emailValue ? email : message;
    if (missing instanceof HTMLElement) missing.focus();
    return false;
  }

  function bindUi() {
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('#checkout, button.cart__checkout-button[name="checkout"]');
        if (!btn || btn.disabled) return;

        e.preventDefault();
        e.stopPropagation();

        Promise.resolve(isGiftMessagePairingComplete(btn)).then((ok) => {
          if (!ok) return;
          if (isEnabled()) {
            proceedToCheckout();
            return;
          }
          const form = document.getElementById('cart-form');
          if (form instanceof HTMLFormElement) {
            form.dataset.giftMessageValidated = 'true';
            form.requestSubmit(btn instanceof HTMLButtonElement ? btn : undefined);
            delete form.dataset.giftMessageValidated;
          }
        });
      },
      true
    );

    document.addEventListener('click', (e) => {
      const card = e.target.closest('[data-reward-option]');
      if (!card) return;
      const root = card.closest('[data-cart-reward-selector]');
      if (!root || root.classList.contains('is-syncing')) return;
      if (document.querySelector('cart-items-component.is-cart-settling')) return;
      e.preventDefault();
      const option = card.getAttribute('data-reward-option');
      if (option) onOptionSelect(option);
    });

    document.addEventListener('cart-drawer:open', () => {
      pushGtm('reward_widget_viewed', { surface: 'cart_drawer' });
      fetchCart().then((cart) => {
        if (cart) {
          updateSelectorUiState(cart, getEffectiveSelectedReward(cart));
        }
      });
    });
  }

  function bindCartEvents() {
    document.addEventListener('cart:update', onCartUpdate);
    if (window.PUB_SUB_EVENTS?.cartUpdate && typeof subscribe === 'function') {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, (payload) => {

        if (isRewardMutationLocked()) return;
        if (payload?.source === SYNC_SOURCE) return;
        if (payload?.cartData) scheduleReconcile(payload.cartData);
      });
    }
  }

  function init() {
    if (!isEnabled()) return;

    refreshExcludedVariantIdsFromDom();
    ensureCartSettlingOverlays();
    bindUi();
    bindCartEvents();
    fetchCart().then((cart) => {
      if (!cart?.items?.length) return;
      pushGtm('reward_widget_viewed', {
        surface: document.body.classList.contains('template-cart') ? 'cart_page' : 'init',
      });
      scheduleReconcile(cart, { force: true, immediate: true });
    });
  }

  window.isCartRewardSelectorEnabled = isEnabled;
  window.getCartRewardLinePropertiesForAdd = getCartRewardLinePropertiesForAdd;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
